import { useCallback } from "react";
import DragIcon from "../icons/DragIcon";
import TrashIcon from "../icons/TrashIcon";
import { Draggable } from "@hello-pangea/dnd";
import MenuItems from "./MenuItems";
import { useSelector } from "react-redux";

/**
 * MenuSection component represents a draggable section within a menu.
 * It allows for editing the section name and managing the items within the section.
 *
 * @component
 * @param {Object} props - The properties object.
 * @param {Object} props.item - The section item data.
 * @param {number} props.index - The index of the section in the list.
 * @param {Object} props.handlerFunctions - The handler functions for updating and deleting sections and items.
 * @param {Function} props.handlerFunctions.onUpdateSection - Function to update the section name.
 * @param {Function} props.handlerFunctions.onItemUpdate - Function to update an item within the section.
 * @param {Function} props.handlerFunctions.onItemDelete - Function to delete an item within the section.
 * @param {Function} props.handlerFunctions.onSectionDelete - Function to delete the section.
 * @param {boolean} props.editMode - Flag indicating if the edit mode is enabled.
 * @returns {JSX.Element} The rendered MenuSection component.
 */
const MenuSection = ({ item, index, handlerFunctions, editMode }) => {

  const isMobile = useSelector((state) => state.mobile.isMobile);

  const section = item;
  const handleInputChange = (event) => {
    handlerFunctions.onUpdateSection(section.id, event.target.value);
  };

  const handleItemUpdate = useCallback(
    (item) => {
      handlerFunctions.onItemUpdate(section, item);
    },
    [section],
  );

  const handleItemDelete = useCallback(
    (item) => {
      handlerFunctions.onItemDetete(section, item);
    },
    [section],
  );

  return (
    <Draggable key={section.id} draggableId={`${section.id}`} index={index}>
      {(parentProvider) => (
        <div
          className="menu-section"
          ref={parentProvider.innerRef}
          {...parentProvider.draggableProps}
        >
          <div className="menu-section-information">
            {editMode && (
              <button
                className="menu-section-button menu-section-drag"
                {...parentProvider.dragHandleProps}
              >
                <DragIcon />
              </button>
            )}
            <input
              className="menu-section-text-input"
              placeholder={section.name}
              value={section.name}
              onChange={handleInputChange}
              onFocus={(e) => e.target.select()}
              disabled={!editMode}
            />
            {editMode && (
              <div className="menu-section-icons">
                <button
                  className="menu-section-button menu-section-trash"
                  onClick={() => handlerFunctions.onSectionDelete(section)}
                >
                  <TrashIcon 
                  iconSize="20"/>
                </button>
              </div>
            )}
          </div>
          <MenuItems
            section={section}
            handleItemUpdate={handleItemUpdate}
            handleItemDelete={handleItemDelete}
            editMode={editMode}
          />
        </div>
      )}
    </Draggable>
  );
};

export default MenuSection;
